import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  createQuestion,
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_TYPES,
} from '@api/instant_survey';
import { maxQuestionError } from '@api/surveys';
import { GATES } from '@constants';
import {
  ActionOverlayPopover,
  Button,
  Card,
  CopyIcon,
  DragDropV2Icon,
  FlowArrowIcon,
  IconButton,
  Layout,
  OverlayPopover,
  OverlayTrigger,
  PencilIcon,
  Pill,
  PlusIcon,
  ShuffleIcon,
  toast,
  Tooltip,
  TrashIcon,
} from '@utilities';
import { MAX_NAME_LENGTH } from '@utilities/helpers/validations';

import ModalAddConcept from './components/ModalAddConcept';
import ModalInstantSurveyQuestion from '../../../ModalInstantSurveyQuestion';
import ModalQuestionLibrary from '../../../ModalQuestionLibrary';
import Question from '../Question';
import ModalDeleteQuestionGroup from '../QuestionGroupBlock/components/ModalDeleteQuestionGroup';
import ModalQuestionGroupLogic from '../QuestionGroupBlock/components/ModalQuestionGroupLogic';
import {
  cleanupLogicAfterEdit,
  cleanupLogicAfterRemoval,
  duplicateSubQuestion,
  stripQuestionIds,
} from '../../../../utilities';
import {
  DEFAULT_MONADIC_SECTION_NAME,
  QUESTION_GROUP_NAME_UNIQUE_ERROR,
} from '../../../../../../utilities/constants';
import { SurveyContext } from '../../../../../../utilities/context';
import {
  getCopiedContainerName,
  getSequentialMonadicQuestionCost,
  getSequentialMonadicQuestionIdsWithIntraSectionLogic,
  getSequentialMonadicSubQuestionCost,
  isQuestionGroupNameTaken,
  isSequentialMonadicGroup,
} from '../../../../../../utilities/helpers';
import { getConceptImageUrl } from './utilities/helpers';

import styles from './_index.module.scss';

export const SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX = 'sequential-monadic-subquestions';
export const SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE = 'SEQUENTIAL_MONADIC_SECTION';
const allowedQuestionTypes = [
  QUESTION_TYPES.RADIO,
  QUESTION_TYPES.CHECKBOX,
  QUESTION_TYPES.ESSAY,
  QUESTION_TYPES.INSTRUCTIONS,
  QUESTION_TYPES['TABLE-RADIO'],
];

const MONADIC_IMAGE_QUESTION_DISABLED_TOOLTIP =
  'Questions with images cannot be added to a monadic section.';

const SequentialMonadicBlock = ({
  canPreviewSurvey,
  firstErrorRef,
  isDetailedQuestionView,
  isPreviewDisabled,
  onPreviewSurvey,
  onRemoveQuestion,
  pipingQuestionsMap,
  previewDisabledTooltip,
  provided,
  question,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const {
    currentQuestionCount,
    deleteGroup,
    instantSurveyType,
    isReadOnly,
    maxQuestions,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);
  const { client } = useStatsigClient();
  const [activeConceptActionsIndex, setActiveConceptActionsIndex] = useState(null);
  // `editingConcept` is a snapshot that drives the modal; `editingConceptActiveIndex` is only a
  // fallback handle for concepts that have never been saved and so have no id to resolve against.
  const [editingConcept, setEditingConcept] = useState(null);
  const [editingConceptActiveIndex, setEditingConceptActiveIndex] = useState(null);
  const [isAddConceptModalOpen, setIsAddConceptModalOpen] = useState(false);
  const [isAddSubQuestionPopoverOpen, setIsAddSubQuestionPopoverOpen] = useState(false);
  const [isCopyingSection, setIsCopyingSection] = useState(false);
  const [isCreateSubQuestionModalOpen, setIsCreateSubQuestionModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isGroupLogicModalOpen, setIsGroupLogicModalOpen] = useState(false);
  const [isQuestionLibraryModalOpen, setIsQuestionLibraryModalOpen] = useState(false);
  const [sectionName, setSectionName] = useState(question.text ?? '');
  const [selectedSubQuestion, setSelectedSubQuestion] = useState(null);
  const [selectedSubQuestionIndex, setSelectedSubQuestionIndex] = useState(null);

  // Keep the input in step when the name changes elsewhere (a draft reload, an undo).
  useEffect(() => {
    setSectionName(question.text ?? '');
  }, [question.text]);
  const activeConcepts = (question.concepts || [])
    .map((concept, conceptIndex) => ({ concept, conceptIndex }))
    .filter(({ concept }) => !concept._delete);
  const activeSubQuestions = (question.subQuestions || []).filter(({ _delete }) => !_delete);
  const conceptCount = activeConcepts.length;
  const hasQuestionLibrary = client.checkGate(GATES.QUESTION_LIBRARY);
  const isModelMigrationEnabled = client.checkGate(GATES.SEQUENTIAL_MONADIC_MODEL_MIGRATION);
  // Only the section's own mirror group may drive group logic or the atomic delete. A
  // container parked in a plain group must not hand that group's id to either.
  const groupId = isSequentialMonadicGroup(survey.questionGroups?.[question.questionGroup])
    ? question.questionGroup
    : null;
  const isRandomized = Boolean(question.properties?.randomizeSubQuestions);
  const randomizeActionLabel = isRandomized
    ? 'Remove question randomization'
    : 'Randomize question order';
  const hasIntraSectionLogic = getSequentialMonadicQuestionIdsWithIntraSectionLogic({
    questionLogic,
    questions: selectedQuestions,
  }).includes(String(question.id));
  const groupLogicBranches = questionLogic?.[QUESTION_GROUP_LOGIC]?.[groupId];
  const hasQuestionGroupLogic = QUESTION_GROUP_LOGIC_BRANCHES.some(
    (branch) => (groupLogicBranches?.[branch]?.logic?.length ?? 0) > 0
  );
  const questionCount = getSequentialMonadicQuestionCost(question);
  const questionCountLabel = `${questionCount} question${questionCount === 1 ? '' : 's'}`;
  const addSubQuestionButtonRef = useRef(null);
  const optionsDropdown = useRef(null);
  const randomizationLabel = question.properties?.randomizeSubQuestions ? 'Randomized. ' : '';
  const subQuestionDroppableId = `${SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX}-${question.id}`;
  const shouldDisableSubQuestionDrag =
    !showActions || activeSubQuestions.length <= 1 || question.properties?.randomizeSubQuestions;

  // A sub-question that does not exist yet costs at least one question per concept; a grid costs
  // more, but its row count is unknowable here. Reserve the optimistic minimum so the button stays
  // enabled, and let the question modal's own save gate reject the real cost once the rows exist.
  const canAddSubQuestion =
    conceptCount === 0 || currentQuestionCount + conceptCount <= maxQuestions;
  const isSubQuestionModalOpen = isCreateSubQuestionModalOpen || Boolean(selectedSubQuestion);
  const maxSubQuestionLibraryQuestions = Math.max(
    0,
    maxQuestions - currentQuestionCount + questionCount
  );
  const wouldCopyExceedMaxQuestions = currentQuestionCount + questionCount > maxQuestions;
  const isCopyDisabled =
    isCopyingSection || activeSubQuestions.length === 0 || wouldCopyExceedMaxQuestions;

  const handleAddConceptModalClose = () => {
    setIsAddConceptModalOpen(false);
  };

  const handleEditConceptModalClose = () => {
    setEditingConcept(null);
    setEditingConceptActiveIndex(null);
  };

  const handleAddConceptClick = () => {
    setIsAddConceptModalOpen(true);
  };

  const handleAddSubQuestionClick = () => {
    if (!hasQuestionLibrary) {
      setIsCreateSubQuestionModalOpen(true);
      return;
    }

    setIsAddSubQuestionPopoverOpen(true);
  };

  const handleCreateSubQuestionClick = () => {
    setIsAddSubQuestionPopoverOpen(false);
    setIsCreateSubQuestionModalOpen(true);
  };

  const handleQuestionLibraryClick = () => {
    setIsAddSubQuestionPopoverOpen(false);
    setIsQuestionLibraryModalOpen(true);
  };

  const handleQuestionLibraryModalClose = () => {
    setIsQuestionLibraryModalOpen(false);
  };

  const handleSubQuestionModalClose = () => {
    setIsCreateSubQuestionModalOpen(false);
    setSelectedSubQuestion(null);
    setSelectedSubQuestionIndex(null);
  };

  const handleSectionRemove = () => {
    setIsDropdownActive(false);
    // A saved section is deleted through the group service so its members and concepts go
    // in one transaction; an unsaved one has no group yet and stays on the draft path.
    if (isModelMigrationEnabled && groupId != null) {
      setIsDeleteModalOpen(true);
      return;
    }
    onRemoveQuestion(question);
  };

  const updateSection = (fields, groupFields) => {
    updateSurvey(
      {
        questions: survey.questions.map((surveyQuestion) =>
          surveyQuestion.id === question.id ? { ...surveyQuestion, ...fields } : surveyQuestion
        ),
        // The mirror derives the group's name and randomize_order from the container on
        // save, so patch the local group too or the logic modal shows stale values.
        ...(groupId != null && {
          questionGroups: {
            ...survey.questionGroups,
            [groupId]: { ...survey.questionGroups[groupId], ...groupFields },
          },
        }),
      },
      { markDirty: true }
    );
  };

  const handleNameBlur = () => {
    const trimmed = sectionName.trim();
    if (!trimmed || trimmed === question.text) {
      setSectionName(question.text ?? '');
      return;
    }
    if (
      isQuestionGroupNameTaken({
        excludeGroupId: groupId,
        name: trimmed,
        questionGroups: survey.questionGroups,
      })
    ) {
      toast.error(QUESTION_GROUP_NAME_UNIQUE_ERROR);
      setSectionName(question.text ?? '');
      return;
    }
    updateSection({ text: trimmed }, { name: trimmed });
  };

  const handleRandomizeToggle = () => {
    updateSection(
      { properties: { ...question.properties, randomizeSubQuestions: !isRandomized } },
      { randomizeOrder: !isRandomized }
    );
    setIsDropdownActive(false);
  };

  const handleSectionCopy = async () => {
    setIsCopyingSection(true);
    setIsDropdownActive(false);
    try {
      const { questionGroup: _questionGroup, ...section } = question;
      const copiedSection = await createQuestion({
        question: stripQuestionIds({
          ...section,
          concepts: activeConcepts.map(({ concept }) => concept),
          subQuestions: activeSubQuestions,
          text: getCopiedContainerName({
            baseName: question.text || DEFAULT_MONADIC_SECTION_NAME,
            questionGroups: survey.questionGroups,
          }),
        }),
      });
      const questions = [...survey.questions];
      questions.splice(questions.findIndex(({ id }) => id === question.id) + 1, 0, copiedSection);
      updateSurvey({ questions }, { markDirty: true });
    } catch (error) {
      console.error('Error copying monadic section', error);
      toast.error('Error copying monadic section');
    } finally {
      setIsCopyingSection(false);
    }
  };

  const getSubQuestionCostForValidation = (subQuestion) =>
    getSequentialMonadicSubQuestionCost(subQuestion) * conceptCount;

  const getActiveAndDeletedSubQuestions = (subQuestions = []) => ({
    activeSubQuestions: subQuestions.filter(({ _delete }) => !_delete),
    deletedSubQuestions: subQuestions.filter(({ _delete }) => _delete),
  });

  const getMatchingActiveSubQuestionIndex = (activeSubQuestions, subQuestion, subQuestionIndex) =>
    subQuestion?.id !== null && subQuestion?.id !== undefined
      ? activeSubQuestions.findIndex(({ id }) => id === subQuestion.id)
      : subQuestionIndex;

  // Autosave responses drop soft-deleted concepts, so raw array indexes are unstable. Saved
  // concepts resolve by id; unsaved concepts use their ordinal among active concepts.
  const getMatchingConceptIndex = (concepts, concept, activeConceptIndex) => {
    if (concept?.id !== null && concept?.id !== undefined) {
      return concepts.findIndex(({ id }) => id === concept.id);
    }
    if (concept?.conceptClientId) {
      return concepts.findIndex(
        ({ conceptClientId }) => conceptClientId === concept.conceptClientId
      );
    }

    return (
      concepts
        .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
        .filter(({ candidate }) => !candidate._delete)[activeConceptIndex]?.candidateIndex ?? -1
    );
  };

  // Older unsaved concepts may not have a client id, so verify a positional fallback still holds
  // what the modal was opened against before writing to it.
  const isMatchingUnsavedConcept = (candidate, concept) =>
    Boolean(candidate) &&
    (concept.conceptClientId
      ? candidate.conceptClientId === concept.conceptClientId
      : candidate.name === concept.name && candidate.image?.id === concept.image?.id);

  const getUpdatedQuestionsWithSubQuestions = (getSubQuestions) =>
    survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      return {
        ...surveyQuestion,
        subQuestions: getSubQuestions(surveyQuestion.subQuestions || []),
      };
    });

  const handleConceptCreate = ({ conceptImage, conceptName }) => {
    if (!conceptName.trim()) return;

    const newConcept = {
      _delete: false,
      image: conceptImage || null,
      name: conceptName.trim(),
    };
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      return {
        ...surveyQuestion,
        concepts: [...(surveyQuestion.concepts || []), newConcept],
      };
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleAddConceptModalClose();
  };

  const handleConceptEdit = (concept, activeConceptIndex) => {
    setEditingConcept(concept);
    setEditingConceptActiveIndex(activeConceptIndex);
    setActiveConceptActionsIndex(null);
  };

  const handleConceptUpdate = ({ conceptImage, conceptName }) => {
    if (!editingConcept || !conceptName.trim()) return;

    let didUpdateConcept = false;
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      const surveyConcepts = surveyQuestion.concepts || [];
      const updateIndex = getMatchingConceptIndex(
        surveyConcepts,
        editingConcept,
        editingConceptActiveIndex
      );

      if (updateIndex < 0 || updateIndex >= surveyConcepts.length) return surveyQuestion;

      const isUnsavedConcept = editingConcept.id === null || editingConcept.id === undefined;
      if (
        isUnsavedConcept &&
        !isMatchingUnsavedConcept(surveyConcepts[updateIndex], editingConcept)
      ) {
        return surveyQuestion;
      }

      didUpdateConcept = true;
      return {
        ...surveyQuestion,
        concepts: surveyConcepts.map((surveyConcept, surveyConceptIndex) => {
          if (surveyConceptIndex !== updateIndex) return surveyConcept;

          return { ...surveyConcept, image: conceptImage || null, name: conceptName.trim() };
        }),
      };
    });

    if (didUpdateConcept) {
      updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    }
    handleEditConceptModalClose();
  };

  const handleConceptRemove = (conceptIndex) => {
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      const updatedConcepts = (surveyQuestion.concepts || []).map(
        (surveyConcept, surveyConceptIndex) => {
          if (surveyConceptIndex !== conceptIndex) return surveyConcept;
          return { ...surveyConcept, _delete: true };
        }
      );

      return {
        ...surveyQuestion,
        concepts: updatedConcepts,
      };
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    setActiveConceptActionsIndex(null);
  };

  const handleSubQuestionCreate = (subQuestion) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);

      return [...activeSubQuestions, subQuestion, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleSubQuestionModalClose();
  };

  const handleLibrarySubQuestionsAdd = (subQuestionsToAdd) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const librarySubQuestions = subQuestionsToAdd.map(duplicateSubQuestion);

      return [...activeSubQuestions, ...librarySubQuestions, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleQuestionLibraryModalClose();
  };

  const handleSubQuestionEdit = (subQuestion, subQuestionIndex) => {
    setSelectedSubQuestion(subQuestion);
    setSelectedSubQuestionIndex(subQuestionIndex);
  };

  const handleSubQuestionDuplicate = (subQuestion, subQuestionIndex) => {
    const subQuestionCost = getSubQuestionCostForValidation(subQuestion);
    if (currentQuestionCount + subQuestionCost > maxQuestions) return;

    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const duplicateIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        subQuestion,
        subQuestionIndex
      );

      if (duplicateIndex < 0) return subQuestions;

      const updatedSubQuestions = [...activeSubQuestions];
      updatedSubQuestions.splice(duplicateIndex + 1, 0, duplicateSubQuestion(subQuestion));

      return [...updatedSubQuestions, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  const handleSubQuestionRemove = (subQuestion, subQuestionIndex) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const removeIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        subQuestion,
        subQuestionIndex
      );

      if (removeIndex < 0) return subQuestions;

      const removedSubQuestion = activeSubQuestions[removeIndex];
      const updatedActiveSubQuestions = activeSubQuestions.filter(
        (_, index) => index !== removeIndex
      );
      const updatedDeletedSubQuestions =
        typeof removedSubQuestion.id === 'number'
          ? [...deletedSubQuestions, { ...removedSubQuestion, _delete: true }]
          : deletedSubQuestions;

      return [...updatedActiveSubQuestions, ...updatedDeletedSubQuestions];
    });

    updateSurvey(
      {
        questionLogic: cleanupLogicAfterRemoval({
          question: subQuestion,
          questionId: subQuestion.id,
          questionLogic: survey.questionLogic || {},
          questions: updatedQuestions,
        }),
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    handleSubQuestionModalClose();
  };

  const handleSubQuestionUpdate = (subQuestion) => {
    if (!selectedSubQuestion) return;

    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const updateIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        selectedSubQuestion,
        selectedSubQuestionIndex
      );

      if (updateIndex < 0) return subQuestions;

      const updatedSubQuestions = activeSubQuestions.map((surveySubQuestion, index) =>
        index === updateIndex ? subQuestion : surveySubQuestion
      );

      return [...updatedSubQuestions, ...deletedSubQuestions];
    });

    updateSurvey(
      {
        questionLogic: cleanupLogicAfterEdit({
          originalQuestion: selectedSubQuestion,
          question: subQuestion,
          questionLogic: survey.questionLogic || {},
          questions: updatedQuestions,
        }),
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    handleSubQuestionModalClose();
  };

  return (
    <div
      className={styles['sequential-monadic-draggable-container']}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      {isAddConceptModalOpen && (
        <ModalAddConcept
          mode="create"
          onClose={handleAddConceptModalClose}
          onCreate={handleConceptCreate}
        />
      )}
      {editingConcept && (
        <ModalAddConcept
          concept={editingConcept}
          mode="edit"
          onClose={handleEditConceptModalClose}
          onUpdate={handleConceptUpdate}
        />
      )}
      {isSubQuestionModalOpen && (
        <ModalInstantSurveyQuestion
          allowedQuestionTypes={allowedQuestionTypes}
          conceptCount={conceptCount}
          getQuestionCostForValidation={getSubQuestionCostForValidation}
          isSequentialMonadicSubQuestion
          onClose={handleSubQuestionModalClose}
          onCreate={handleSubQuestionCreate}
          onUpdate={handleSubQuestionUpdate}
          originalQuestion={selectedSubQuestion}
          pipingQuestionsMap={pipingQuestionsMap}
          shouldPersistOnCreate={false}
          shouldPersistOnUpdate={false}
        />
      )}
      {isDeleteModalOpen && (
        <ModalDeleteQuestionGroup
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={async () => {
            await deleteGroup(groupId);
            setIsDeleteModalOpen(false);
          }}
        />
      )}
      {isGroupLogicModalOpen && (
        <ModalQuestionGroupLogic
          onClose={() => setIsGroupLogicModalOpen(false)}
          questionGroups={Object.values(survey.questionGroups || {})}
          questionLogic={questionLogic || {}}
          questions={selectedQuestions}
          setQuestionLogic={setQuestionLogic}
        />
      )}
      {isQuestionLibraryModalOpen && (
        <ModalQuestionLibrary
          allowedQuestionTypes={allowedQuestionTypes}
          areImageQuestionsDisabled
          country={survey.country}
          getQuestionCostForValidation={getSubQuestionCostForValidation}
          imageQuestionsDisabledTooltip={MONADIC_IMAGE_QUESTION_DISABLED_TOOLTIP}
          maxQuestions={maxSubQuestionLibraryQuestions}
          onClose={handleQuestionLibraryModalClose}
          onSelectQuestions={handleLibrarySubQuestionsAdd}
          selectedQuestions={activeSubQuestions}
          surveyType={instantSurveyType}
        />
      )}

      <Card
        as={Layout.Flex.Column}
        className={styles['sequential-monadic-card']}
        elevation={2}
        gap="small"
      >
        <div className={styles['sequential-monadic-header']}>
          <div className={styles['sequential-monadic-title-wrapper']}>
            {!isReadOnly && (
              <div
                className={styles['sequential-monadic-drag-handle']}
                {...provided.dragHandleProps}
              >
                <DragDropV2Icon />
              </div>
            )}
            {hasQuestionGroupLogic && (
              <OverlayTrigger.Tooltip
                overlay={<Tooltip>This monadic section has logic applied</Tooltip>}
                placement="top"
              >
                <div className={styles['sequential-monadic-logic-icon']}>
                  <FlowArrowIcon />
                </div>
              </OverlayTrigger.Tooltip>
            )}
            {isModelMigrationEnabled ? (
              <input
                aria-label="Monadic section name"
                className={styles['sequential-monadic-name-input']}
                disabled={isReadOnly || !showActions}
                maxLength={MAX_NAME_LENGTH}
                onBlur={handleNameBlur}
                onChange={(event) => setSectionName(event.target.value)}
                type="text"
                value={sectionName}
              />
            ) : (
              <h3>Monadic Section</h3>
            )}
            <ShuffleIcon />
            <Pill isCompleted value={questionCountLabel} />
          </div>
          {showActions && (
            <div className={styles['sequential-monadic-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
                target={optionsDropdown.current}
              >
                <ul>
                  {isModelMigrationEnabled && (
                    <>
                      <li>
                        <OverlayTrigger.Tooltip
                          overlay={
                            hasIntraSectionLogic ? (
                              <Tooltip className={styles['sequential-monadic-action-tooltip']}>
                                Randomization cannot be used because there is logic applied to a
                                question within this section
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                          placement="bottom"
                        >
                          <button
                            aria-label={randomizeActionLabel}
                            data-testid="randomize-monadic-section-button"
                            disabled={hasIntraSectionLogic}
                            onClick={handleRandomizeToggle}
                          >
                            <ShuffleIcon />
                            {randomizeActionLabel}
                          </button>
                        </OverlayTrigger.Tooltip>
                      </li>
                      <li>
                        <button
                          aria-label="Question Group Logic"
                          data-testid="monadic-section-logic-button"
                          disabled={groupId == null}
                          onClick={() => {
                            setIsGroupLogicModalOpen(true);
                            setIsDropdownActive(false);
                          }}
                        >
                          <FlowArrowIcon />
                          Question Group Logic
                        </button>
                      </li>
                      <li>
                        <OverlayTrigger.Tooltip
                          overlay={
                            wouldCopyExceedMaxQuestions ? (
                              <Tooltip className={styles['sequential-monadic-action-tooltip']}>
                                {maxQuestionError}
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                          placement="bottom"
                        >
                          <button
                            aria-label="Copy section"
                            data-testid="copy-monadic-section-button"
                            disabled={isCopyDisabled}
                            onClick={handleSectionCopy}
                          >
                            <CopyIcon />
                            Copy Section
                          </button>
                        </OverlayTrigger.Tooltip>
                      </li>
                    </>
                  )}
                  <li>
                    <button
                      aria-label="Remove monadic section"
                      data-testid="remove-sequential-monadic-section-button"
                      onClick={handleSectionRemove}
                    >
                      <TrashIcon />
                      <span>Remove Section</span>
                    </button>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </div>

        <p className={styles['sequential-monadic-copy']}>
          This section of questions will show the following concepts.
        </p>
        <p className={styles['sequential-monadic-copy']}>
          {randomizationLabel}Panelists will see all of these questions {conceptCount} time
          {conceptCount === 1 ? '' : 's'} based on the following concepts:
        </p>

        {showActions && (
          <Button
            className={styles['sequential-monadic-add-concept']}
            icon={<PlusIcon />}
            onClick={handleAddConceptClick}
            text="Add Concept"
            variant="secondary"
          />
        )}

        {activeConcepts.length > 0 && (
          <ol className={styles['sequential-monadic-concepts']}>
            {activeConcepts.map(({ concept, conceptIndex }, index) => {
              const imageUrl = getConceptImageUrl(concept.image);

              const conceptName = concept.name || `Concept ${index + 1}`;
              // the image is decorative here: the card's own label already names the concept
              const conceptBody = (
                <>
                  <span className={styles['sequential-monadic-concept-image-wrapper']}>
                    {imageUrl && (
                      <img
                        alt=""
                        className={styles['sequential-monadic-concept-image']}
                        src={imageUrl}
                      />
                    )}
                  </span>
                  <span className={styles['sequential-monadic-concept-name']}>{conceptName}</span>
                </>
              );

              return (
                <li
                  className={styles['sequential-monadic-concept']}
                  key={concept.id ?? conceptIndex}
                >
                  {showActions && (
                    <div className={styles['sequential-monadic-concept-actions']}>
                      <ActionOverlayPopover
                        hasArrow={false}
                        isActive={activeConceptActionsIndex === conceptIndex}
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveConceptActionsIndex(
                            activeConceptActionsIndex === conceptIndex ? null : conceptIndex
                          );
                        }}
                        onHide={() => setActiveConceptActionsIndex(null)}
                      >
                        <ul>
                          <li>
                            <button
                              aria-label={`Edit ${conceptName}`}
                              data-testid="edit-concept-button"
                              onClick={() => handleConceptEdit(concept, index)}
                            >
                              <PencilIcon />
                              <span>Edit Concept</span>
                            </button>
                          </li>
                          <li>
                            <button
                              aria-label={`Remove ${conceptName}`}
                              data-testid="remove-concept-button"
                              onClick={() => handleConceptRemove(conceptIndex)}
                            >
                              <TrashIcon />
                              <span>Remove Concept</span>
                            </button>
                          </li>
                        </ul>
                      </ActionOverlayPopover>
                    </div>
                  )}
                  {showActions ? (
                    <button
                      aria-label={`Edit concept: ${conceptName}`}
                      className={styles['sequential-monadic-concept-body-button']}
                      onClick={() => handleConceptEdit(concept, index)}
                      type="button"
                    >
                      {conceptBody}
                    </button>
                  ) : (
                    conceptBody
                  )}
                </li>
              );
            })}
          </ol>
        )}

        <div className={styles['sequential-monadic-questions']}>
          {showActions && activeSubQuestions.length === 0 && (
            <button
              className={styles['sequential-monadic-subquestion-placeholder']}
              onClick={handleCreateSubQuestionClick}
              type="button"
            >
              <span
                aria-hidden
                className={styles['sequential-monadic-subquestion-placeholder-drag-handle']}
              >
                <DragDropV2Icon />
              </span>
              <span className={styles['sequential-monadic-subquestion-placeholder-number']}>1</span>
              <span className={styles['sequential-monadic-subquestion-placeholder-text']}>
                Click to create a question
              </span>
            </button>
          )}

          {activeSubQuestions.length > 0 && (
            <Droppable
              droppableId={subQuestionDroppableId}
              isDropDisabled={shouldDisableSubQuestionDrag}
              type={SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE}
            >
              {(droppableProvided) => (
                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {activeSubQuestions.map((subQuestion, index) => {
                    const subQuestionId = subQuestion.id ?? index;
                    const number =
                      subQuestion.type === QUESTION_TYPES.INSTRUCTIONS.value
                        ? 'T'
                        : activeSubQuestions
                            .slice(0, index)
                            .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
                            .length + 1;

                    return (
                      <Draggable
                        draggableId={`sequential-monadic-subquestion-${question.id}-${subQuestionId}`}
                        index={index}
                        isDragDisabled={shouldDisableSubQuestionDrag}
                        key={subQuestionId}
                      >
                        {(draggableProvided) => (
                          <Question
                            canPreviewSurvey={canPreviewSurvey}
                            conceptCount={conceptCount}
                            firstErrorRef={firstErrorRef}
                            index={index}
                            isDetailedQuestionView={isDetailedQuestionView}
                            isPreviewDisabled={isPreviewDisabled}
                            isSequentialMonadicSubQuestion
                            number={number}
                            onDuplicateQuestion={() =>
                              handleSubQuestionDuplicate(subQuestion, index)
                            }
                            onEditQuestion={() => handleSubQuestionEdit(subQuestion, index)}
                            onPreviewSurvey={onPreviewSurvey}
                            onRemoveQuestion={() => handleSubQuestionRemove(subQuestion, index)}
                            previewDisabledTooltip={previewDisabledTooltip}
                            provided={draggableProvided}
                            question={subQuestion}
                            questionLogic={questionLogic || {}}
                            selectedQuestions={selectedQuestions}
                            setQuestionLogic={setQuestionLogic}
                            showActions={showActions}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {showActions && (
            <div className={styles['sequential-monadic-add-subquestion-wrapper']}>
              <IconButton
                aria-label="Add monadic subquestion"
                className={styles['sequential-monadic-add-subquestion']}
                isDisabled={!canAddSubQuestion}
                onClick={handleAddSubQuestionClick}
                ref={addSubQuestionButtonRef}
                tooltip={canAddSubQuestion ? 'Add Question' : maxQuestionError}
                type="button"
              >
                <PlusIcon />
              </IconButton>
              {hasQuestionLibrary && (
                <OverlayPopover
                  className={styles['sequential-monadic-add-subquestion-dropdown']}
                  isActive={isAddSubQuestionPopoverOpen}
                  onHide={() => setIsAddSubQuestionPopoverOpen(false)}
                  target={addSubQuestionButtonRef.current}
                >
                  <ul>
                    <li>
                      <button onClick={handleCreateSubQuestionClick}>
                        <span>Create Question</span>
                        <PlusIcon />
                      </button>
                    </li>
                    <li>
                      <button onClick={handleQuestionLibraryClick}>
                        <span>Add From Library</span>
                        <PlusIcon />
                      </button>
                    </li>
                  </ul>
                </OverlayPopover>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

SequentialMonadicBlock.propTypes = {
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isPreviewDisabled: PropTypes.bool,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  pipingQuestionsMap: PropTypes.object.isRequired,
  previewDisabledTooltip: PropTypes.string,
  provided: PropTypes.object,
  question: PropTypes.object.isRequired,
  questionLogic: PropTypes.object,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
};

export default SequentialMonadicBlock;
